import {
    PlusIcon,
    VerticalDotsIcon,
    SearchIcon,
    ChevronDownIcon
} from "@/components/common/Icons";
import capitalizeFirstLetter from "@/utils/Capitalize";
import {
    Button,
    Chip,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    Input,
    Pagination,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    User,
    Image,
} from "@heroui/react";
import Link from "next/link";
import React, { useEffect } from "react";
import axios from "axios";
import { findNearestPolishLocation, formatNearestLocation, parseCoordinatesAndFindLocation } from "@/utils/findNearestPolishLocation";

export const columns = [
    { name: "OKŁADKA", uid: "thumbnail" },
    { name: "TYTUŁ", uid: "title", sortable: true },
    { name: "BIBLIOTEKA", uid: "name", sortable: true },
    { name: "PROWADZI", uid: "nickname" },
    { name: "MIASTO", uid: "city", sortable: true },
    { name: "WOJEWODZTWO", uid: "county" },
    { name: "STATUS", uid: "status", sortable: true },
    { name: "Pożycz", uid: "actions" },
];

export const statusOptions = [
    { name: "Do wypożyczenia", uid: "active" },
    { name: "Wypożyczona", uid: "paused" },
];

const INITIAL_VISIBLE_COLUMNS = ["thumbnail", "title", "name", "city", "status", "actions"];

export default function ListOfBookCopies({
    bookCopies = [],
    count = 0,
    userId = null,
    isbnId = null,
    customColumns = null,
    placeholder = "Szukaj...",
    hasAddButton = true
}) {
    const [filterValue, setFilterValue] = React.useState("");
    const [visibleColumns, setVisibleColumns] = React.useState(
        new Set(customColumns ? customColumns : INITIAL_VISIBLE_COLUMNS)
    );
    const [statusFilter, setStatusFilter] = React.useState("all");
    const [rowsPerPage, setRowsPerPage] = React.useState(20); // Backend returns 20 items per page
    const [sortDescriptor, setSortDescriptor] = React.useState({
        column: "title",
        direction: "ascending",
    });
    const [page, setPage] = React.useState(1);
    const [isLoading, setIsLoading] = React.useState(false);
    const [data, setData] = React.useState(bookCopies);
    const [totalCount, setTotalCount] = React.useState(count || bookCopies.length);

    // Calculate total pages based on backend's total count, not just current data
    const pages = Math.ceil(totalCount / rowsPerPage);

    // Handle pagination and search from backend
    useEffect(() => {
        const fetchData = async () => {
            if (!userId && !isbnId) {
                // If no filters are provided, use the initial data passed to the component
                return;
            }

            setIsLoading(true);

            try {
                let url = `${process.env.NEXT_PUBLIC_DJANGO_URL}/api/bookle/bookcopies?page=${page}`;

                if (userId) {
                    url += `&user=${userId}`;
                }

                if (isbnId) {
                    url += `&isbn=${isbnId}`;
                }

                if (filterValue) {
                    url += `&q=${filterValue}`;
                }

                const response = await axios.get(url);
                setData(response.data.results);
                setTotalCount(response.data.count);
            } catch (error) {
                console.error('Error fetching book copies:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [page, filterValue, userId, isbnId]);

    const hasSearchFilter = Boolean(filterValue);

    const headerColumns = React.useMemo(() => {
        if (visibleColumns === "all") return columns;

        return columns.filter((column) => Array.from(visibleColumns).includes(column.uid));
    }, [visibleColumns]);

    // Local filtering for status - only needed if not handled by backend
    const filteredItems = React.useMemo(() => {
        let filteredBooks = [...data];

        // Status filtering is done locally since it's easier to manage in the UI
        if (statusFilter !== "all" && Array.from(statusFilter).length !== statusOptions.length) {
            filteredBooks = filteredBooks.filter((bookCopy) => {
                if (Array.from(statusFilter).includes("active")) {
                    return bookCopy.is_available
                }
                if (Array.from(statusFilter).includes("paused")) {
                    return !bookCopy.is_available
                }
            }
            );
        }

        return filteredBooks;
    }, [data, statusFilter]);

    // The items are already paginated by the backend
    const items = filteredItems;

    // Local sorting - can be moved to backend parameters if needed
    const sortedItems = React.useMemo(() => {
        return [...items].sort((a, b) => {
            const first = a[sortDescriptor.column];
            const second = b[sortDescriptor.column];
            const cmp = first < second ? -1 : first > second ? 1 : 0;

            return sortDescriptor.direction === "descending" ? -cmp : cmp;
        });
    }, [sortDescriptor, items]);

    const renderCell = React.useCallback((obj, columnKey) => {
        const cellValue = obj[columnKey];
        switch (columnKey) {
            case "thumbnail":
                return (
                    <Image
                        width={40}
                        height={60}
                        alt={obj.isbn?.title || "Okładka książki"}
                        src={obj.isbn?.thumbnailMedium || require('@/assets/images/book_cover_unavailable.webp').default.src}
                        className="min-w-[40px] object-cover rounded-sm"
                    />
                );
            case "title":
                return (
                    <div className="flex flex-col">
                        <Link
                            href={`/book/${obj.isbn?.slug || obj.isbn_id}`}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                            {obj.isbn?.title || "Tytuł nieznany"}
                        </Link>
                        <p className="text-tiny text-default-500">{obj.isbn?.author || ""}</p>
                    </div>
                );
            case "name":
                return (
                    <User
                        avatarProps={{ radius: "sm", size: "md" }}
                        classNames={{
                            description: "text-default-500",
                        }}
                        description={
                            <Link className="text-blue-500 font-semibold" href={`/u/${obj.user.nickname}`}>
                                @{obj.user.nickname}
                            </Link>
                        }
                        name={`${obj.user.first_name} ${obj.user.last_name}` || obj.user.nickname}
                    >
                        {`${obj.user.first_name} ${obj.user.last_name}` || obj.user.nickname}
                    </User>
                );
                case "city":
                    return (
                        <div className="flex flex-col">
                            {obj.coordinates ? (
                                <>
                                    <p className="text-bold text-small capitalize">
                                        ~{parseCoordinatesAndFindLocation(obj.coordinates, 'city')}
                                    </p>
                                    <p className="text-bold text-tiny capitalize text-default-500">
                                       {parseCoordinatesAndFindLocation(obj.coordinates, 'county')}
                                    </p>
                                </>
                            ) : (
                                <p className="text-bold text-small text-default-500">Lokalizacja nieznana</p>
                            )}
                        </div>
                    );
            case "status":
                return (
                    <Chip
                        className="capitalize border-none gap-1 text-default-600"
                        color={obj.is_available ? "success" : "danger"}
                        size="sm"
                        variant="dot"
                    >
                        {obj.is_available ? "Do wypożyczenia" : "Wypożyczona"}
                    </Chip>
                );
            case "actions":
                return (
                    <div className="relative flex justify-end items-center gap-2">
                        <Dropdown className="bg-background border-1 border-default-200">
                            <DropdownTrigger>
                                <Button isIconOnly radius="full" size="sm" variant="light">
                                    <VerticalDotsIcon className="text-default-400" />
                                </Button>
                            </DropdownTrigger>
                            <DropdownMenu>
                                <DropdownItem key="details">  <Link href={`/bookle/${obj.uid}`}>Sprawdź Egzemplarz</Link></DropdownItem>
                                {/* <DropdownItem key="map">Na mapie</DropdownItem> */}
                            </DropdownMenu>
                        </Dropdown>
                    </div>
                );
            default:
                return cellValue;
        }
    }, []);

    const onRowsPerPageChange = React.useCallback((e) => {
        setRowsPerPage(Number(e.target.value));
        setPage(1);
    }, []);

    // Debounced search handler
    const onSearchChange = React.useCallback((value) => {
        if (value) {
            setFilterValue(value);
            setPage(1);
        } else {
            setFilterValue("");
        }
    }, []);

    const topContent = React.useMemo(() => {
        return (
            <div className="flex flex-col gap-4">
                <div className="flex justify-between gap-3 items-end">
                    <Input
                        id="search"
                        isClearable
                        classNames={{
                            base: "w-full",
                            inputWrapper: "border-1",
                        }}
                        placeholder={placeholder}
                        size="sm"
                        startContent={
                            isLoading ? (
                                <Spinner size="sm" color="default" className="mr-2" />
                            ) : (
                                <SearchIcon className="text-default-300" />
                            )
                        }
                        value={filterValue}
                        variant="bordered"
                        onClear={() => setFilterValue("")}
                        onValueChange={onSearchChange}
                    />
                    <div className="flex gap-3">
                        <Dropdown>
                            <DropdownTrigger className="hidden sm:flex">
                                <Button
                                    endContent={<ChevronDownIcon className="text-small" />}
                                    size="sm"
                                    variant="flat"
                                >
                                    Status
                                </Button>
                            </DropdownTrigger>
                            <DropdownMenu
                                disallowEmptySelection
                                aria-label="Table Columns"
                                closeOnSelect={false}
                                selectionMode="multiple"
                                onSelectionChange={setStatusFilter}
                            >
                                {statusOptions.map((status) => (
                                    <DropdownItem key={status.uid} className="capitalize">
                                        {capitalizeFirstLetter(status.name)}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </Dropdown>
                        <Dropdown>
                            <DropdownTrigger className="hidden sm:flex">
                                <Button
                                    endContent={<ChevronDownIcon className="text-small" />}
                                    size="sm"
                                    variant="flat"
                                >
                                    Kolumny
                                </Button>
                            </DropdownTrigger>
                            <DropdownMenu
                                disallowEmptySelection
                                aria-label="Table Columns"
                                closeOnSelect={false}
                                selectionMode="multiple"
                                onSelectionChange={setVisibleColumns}
                            >
                                {columns.map((column) => (
                                    <DropdownItem key={column.uid} className="capitalize">
                                        {capitalizeFirstLetter(column.name)}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </Dropdown>
                       {hasAddButton && (<Button
                            className="bg-foreground text-background"
                            endContent={<PlusIcon />}
                            size="sm"
                            as={Link}
                            href="/kody"
                        >
                            Dodaj własną
                        </Button>)}
                    </div>
                </div>
                <div className="flex justify-between items-center">
                    <span className="text-default-400 text-small">
                        Znaleziono {totalCount} egzemplarze (wyświetlanie {(page - 1) * rowsPerPage + 1}-{Math.min(page * rowsPerPage, totalCount)})
                    </span>
                    <label className="flex items-center text-default-400 text-small">
                        Egzemplarze na stronie:
                        <select
                            className="bg-transparent outline-none text-default-400 text-small ml-1"
                            onChange={onRowsPerPageChange}
                            value={rowsPerPage}
                        >
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                        </select>
                    </label>
                </div>
            </div>
        );
    }, [
        filterValue,
        statusFilter,
        visibleColumns,
        onSearchChange,
        onRowsPerPageChange,
        totalCount,
        page,
        rowsPerPage,
        isLoading,
    ]);

    const bottomContent = React.useMemo(() => {
        return (
            <div className="py-2 px-2 flex justify-between items-center">
                <Pagination
                    showControls
                    classNames={{
                        cursor: "bg-foreground text-background",
                    }}
                    color="default"
                    isDisabled={isLoading}
                    page={page}
                    total={pages}
                    variant="light"
                    onChange={setPage}
                />
                {isLoading && (
                    <div className="flex items-center gap-2">
                        <Spinner size="sm" />
                        <span className="text-default-400 text-small">Ładowanie...</span>
                    </div>
                )}
            </div>
        );
    }, [page, pages, isLoading]);

    const classNames = React.useMemo(
        () => ({
            wrapper: ["max-h-[382px]", "max-w-3xl"],
            th: ["bg-transparent", "text-default-500", "border-b", "border-divider"],
            td: [
                // changing the rows border radius
                // first
                "group-data-[first=true]/tr:first:before:rounded-none",
                "group-data-[first=true]/tr:last:before:rounded-none",
                // middle
                "group-data-[middle=true]/tr:before:rounded-none",
                // last
                "group-data-[last=true]/tr:first:before:rounded-none",
                "group-data-[last=true]/tr:last:before:rounded-none",
            ],
        }),
        [],
    );

    return (
        <Table
            isCompact
            removeWrapper
            aria-label="Table with book copies"
            bottomContent={bottomContent}
            bottomContentPlacement="outside"
            checkboxesProps={{
                classNames: {
                    wrapper: "after:bg-foreground after:text-background text-background",
                },
            }}
            classNames={classNames}
            sortDescriptor={sortDescriptor}
            topContent={topContent}
            topContentPlacement="outside"
            onSortChange={setSortDescriptor}
            loadingContent={<Spinner label="Ładowanie..." />}
            isLoading={isLoading}
        >
            <TableHeader columns={headerColumns}>
                {(column) => (
                    <TableColumn
                        key={column.uid}
                        align={column.uid === "actions" ? "center" : "start"}
                        allowsSorting={column.sortable}
                    >
                        {column.name}
                    </TableColumn>
                )}
            </TableHeader>
            <TableBody
                emptyContent={isLoading ? "Ładowanie danych..." : "Nie znaleziono egzemplarzy"}
                items={sortedItems}
            >
                {(item) => (
                    <TableRow key={item.id || item.uid}>
                        {(columnKey) => <TableCell>{renderCell(item, columnKey)}</TableCell>}
                    </TableRow>
                )}
            </TableBody>
        </Table>
    );
}

